import React, { memo } from 'react';
import {
  TableCell, Box,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import { theme } from '../../../theme';
import { applyRoundAndFormat, getInventorySku } from '../../../utils';
import Tooltip from '@mui/material/Tooltip';
import { PredictedDemandModel } from '../../../app/services/api/podClient';
import './OrderForm.css';
import { OrderFormItem, DeliveryDates, OrderFormItemUpdate } from '../../../app/services/api/orderManagementClient';
import QuantityCell from './QuantityCell';
import { isSameDayOrderForm } from '../utils';
import NewItemBadge from '../../../components/shared/orders/NewItemBadge';


interface OrderFormRowProps {
  item: OrderFormItem;
  dates: DeliveryDates[];
  handleSingleQuantityChange: (
    newQuantity: number,
    sku: string,
    itemId: number,
    dateIndex: number
  ) => Promise<void>;
  canSaveOrder: boolean;
  isQuantityError: (item: OrderFormItem, currentValue: string) => boolean;
  quantityError: (item: OrderFormItem, currentValue: string) => string | null;
  showMessageWhenNoCost: boolean;
  currencyCode: string;
  t: (key: string) => string;
  predictedDemands: PredictedDemandModel[];
  orderType: number;
  handleMagicWandClicked: (predictedQuantity: number, item: OrderFormItem, index: number) => void;
  upsertOrderFormItemUpdate: (single: OrderFormItemUpdate) => void;
  getItemUpdate: (item: OrderFormItem) => OrderFormItemUpdate | undefined;
}

const OrderFormRow: React.FC<OrderFormRowProps> = memo(function OrderFormRow({
  item,
  orderType,
  dates,
  getItemUpdate,
  handleSingleQuantityChange,
  canSaveOrder,
  isQuantityError,
  quantityError,
  showMessageWhenNoCost,
  currencyCode,
  t,
  predictedDemands,
  handleMagicWandClicked,
  upsertOrderFormItemUpdate,
}) {
  const current = getItemUpdate(item);

  const ProductInfoCell = () => (
    <TableCell sx={{ MaxWidth: '20%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <Stack direction='row'
        spacing={6}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}>
        <Box
          sx={{
            width: '100px',
            height: '100px',
            justifyContent: 'center',
            backgroundColor: (item?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #ddd',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            imageRendering: '-webkit-optimize-contrast',
            backgroundImage: `url(${item.thumbnailUrl ? item.thumbnailUrl : ''})`,
          }}
        />
        <Box sx={{ maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Box display="flex"
            alignItems="center">
            <Tooltip title={item.name}
              arrow>
              <Typography variant='textXS'
                display="block"
                fontWeight={600}>
                {item.isFrozen ? '❄️ ' : ''}{item.name}
              </Typography>
            </Tooltip>
            <NewItemBadge isNew={item.isNewItem} />
          </Box>
          <Typography variant='textXS'
            display="block"
            fontWeight={400}>
            {item.sku}
          </Typography>
          <Typography variant='textXS'
            display="block">
            {t('orderableUOM')} {item.orderableUom} ({item.packSize}
            {item.primaryUom})
          </Typography>
          <Typography variant='textXS'
            display="block">
            {t('primaryUOM')} {item.primaryUom}
          </Typography>
          <Box>
            <Tooltip title={`${t('stockOnHand')}: ${item.stockOnHand}`}
              arrow>
              <Chip
                size="small"
                color="primary"
                variant='outlined'
                label={`${t('stockOnHandABR')} ${item.stockOnHand}`}
                sx={{ mr: 2 }}
              />
            </Tooltip>
            <Tooltip title={`${t('stockOnOrder')}: ${item.stockOnOrder}`}
              arrow>
              <Chip
                size="small"
                color="warning"
                variant='outlined'
                label={`${t('StockOnOrderABR')} ${item.stockOnOrder}`}
                sx={{ mr: 2 }}
              />
            </Tooltip>
            {!(showMessageWhenNoCost || item.price === 0) && <Chip
              size="small"
              color="info"
              variant='outlined'
              label={applyRoundAndFormat(item.price, currencyCode)}
              sx={{ mr: 2 }}
            />}
          </Box>
        </Box>
      </Stack>
    </TableCell >
  );

  // QuantityCell for each date
  const renderQuantityCells = dates.map((dateObj, index) => {
    const date = dateObj.deliveryDate;
    const originalQty = item.quantity[index].toString();
    const updatedQty =
      current?.dates.find((d) => d.date === date)?.quantity ??
      originalQty;
    const currentValue = updatedQty.toString();

    const podItem = predictedDemands.find((x) => x.itemNumber === getInventorySku(item));
    const predictedItem = podItem?.predictedDemandItems?.find(p =>
      isSameDayOrderForm(p.date, date),
    );
    const hasPrediction = predictedItem?.hasPrediction;
    const predictedQuantity = hasPrediction ? predictedItem.quantity : 0;
    const matchesPredictedValue = predictedQuantity.toString() === currentValue;
    const borderColor = hasPrediction && matchesPredictedValue ? theme.palette.success[400] : '';

    const predictedDemandQuantity = predictedItem?.predictedDemandQuantity ?? undefined;
    const safetyStock = predictedItem?.safetyStock ?? undefined;

    return (
      <TableCell key={`${item.id}-${orderType}-${date}`}
        align="center">
        <QuantityCell
          item={item}
          dateIndex={index}
          key={`quantityCell-${item.id}-${orderType}-${date}`}
          currentValue={currentValue}
          handleSingleQuantityChange={handleSingleQuantityChange}
          canSave={canSaveOrder}
          isError={isQuantityError(item, currentValue)}
          helperText={quantityError(item, currentValue)}
          showPrediction={hasPrediction}
          predictedQuantity={predictedQuantity}
          onMagicWandClick={() => handleMagicWandClicked(predictedQuantity, item, index)}
          borderColor={borderColor}
          predictedDemandQuantity={predictedDemandQuantity}
          safetyStock={safetyStock}
          primaryUom={item.primaryUom}
          date={date}
          upsertOrderFormItemUpdate={upsertOrderFormItemUpdate}
        />
      </TableCell>
    );
  });

  return (
    <>
      <ProductInfoCell />
      {renderQuantityCells}
    </>
  );
});

export default OrderFormRow;
